// helpdesk-frontend/src/components/common/PrzewijanieTabeli/PrzewijanieTabeli.js
//
// [polecenie właściciela 2026-09-18] „do KAŻDEJ tabeli w aplikacji — każdej bez wyjątku — dodaj opcję
// przewijania jej w bok, przyciskami lewo-prawo, tak jak w /admin/users. Ma to być dynamicznie określane
// na podstawie tego, czy tabela się mieści, czy nie na ekranie.”
//
// CO BYŁO ŹLE. W /admin/users przyciski były wpisane ręcznie w jednej stronie: renderowały się ZAWSZE
// (także gdy tabela mieściła się w całości — człowiek klikał strzałkę i nic się nie działo), nie gasły na
// krańcach (na lewej krawędzi strzałka „w lewo” była martwa, bez żadnej informacji), a krok 420 px nie zależał
// od szerokości okna. Pozostałe ~120 tabel nie miało przycisków wcale — na ciasnym ekranie (szuflada po prawej,
// zwinięty panel boczny, mały laptop) jedynym sposobem był poziomy pasek przewijania u DOŁU kontenera,
// niewidoczny, dopóki nie przewinie się tabeli do końca w pionie.
//
// JAK DZIAŁA. Jeden komponent wstawiany BEZPOŚREDNIO PRZED `<table>` wewnątrz jej istniejącej otoczki
// (`.tableWrap`, `.tableScroll`, `.tabelaOtoczka`, `.tableContainer`, …). Otoczka zostaje TĄ SAMĄ otoczką —
// żadnego nowego elementu między nią a tabelą, więc zapadki pilnujące `table.parentElement` (ITAM, SIM, MPK,
// sejf) nie widzą różnicy. Komponent sam znajduje kontener (`parentElement`), mierzy `scrollWidth` vs
// `clientWidth` i:
//   • renderuje pasek TYLKO, gdy tabela nie mieści się (różnica > 1 px) i kontener jest widoczny
//     (`clientWidth > 0` — na telefonie otoczki są `display: none`, a pokazują się karty),
//   • mierzy na żywo: ResizeObserver na kontenerze ORAZ na tabeli (dane wczytują się po montażu —
//     pusta tabela mieści się, po fetchu już nie), MutationObserver na podmianę tabeli, `scroll` na
//     kontenerze (stan przycisków), `resize` okna (zapas dla środowisk bez ResizeObserver),
//   • wyłącza przycisk na krańcu zakresu (`disabled` + aria-disabled), krok = ~80 % widocznej szerokości
//     minus szerokość przyklejonych kolumn (inaczej pierwsza przewinięta kolumna chowała się pod
//     przyklejoną kolumną ID/osoby),
//   • jeśli otoczka nie przewija w ogóle (tabela bez dedykowanej otoczki, `overflow: visible` — tabela
//     wylewała się poza kartę), nadaje jej `overflow-x: auto` DOPIERO gdy tabela faktycznie nie mieści się.
//
// ⚠️ jsdom (testy): nie ma ResizeObserver, `scrollWidth === clientWidth === 0`, `Element.scrollBy` nie istnieje.
// Komponent nie może się wywrócić bez obserwatora (inaczej padłoby ~100 testów stron z tabelami) — dlatego
// każdy obserwator jest za `typeof … === "function"`, a przewijanie ma zapas `scrollLeft +=`.
// Pasek jest sticky w lewo (`left: 0`), więc przy przewiniętej tabeli zostaje w oknie.

import React, { useCallback, useEffect, useRef, useState } from "react";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import p from "./PrzewijanieTabeli.module.css";

/** Ile px różnicy scrollWidth − clientWidth traktujemy jako „mieści się” (zaokrąglenia subpikselowe). */
const TOLERANCJA_PX = 1;
/** Minimalny krok przewinięcia (px) — żeby przy wąskiej otoczce przycisk nie „stał w miejscu”. */
const MIN_KROK_PX = 120;

const STAN_POCZATKOWY = { mozna: false, naLewo: false, naPrawo: false };

const kontenerPrzewija = (el) => {
    if (typeof window === "undefined" || typeof window.getComputedStyle !== "function") return true;
    const ox = window.getComputedStyle(el).overflowX;
    return ox === "auto" || ox === "scroll";
};

/** Zmierz kontener i powiedz, czy da się przewijać i w którą stronę. */
export const zmierzPrzewijanie = (el) => {
    if (!el) return STAN_POCZATKOWY;
    const clientWidth = el.clientWidth || 0;
    const scrollWidth = el.scrollWidth || 0;
    const scrollLeft = el.scrollLeft || 0;
    const mozna = clientWidth > 0 && scrollWidth - clientWidth > TOLERANCJA_PX;
    if (!mozna) return STAN_POCZATKOWY;
    return {
        mozna: true,
        naLewo: scrollLeft > TOLERANCJA_PX,
        naPrawo: scrollLeft + clientWidth < scrollWidth - TOLERANCJA_PX,
    };
};

/** Suma szerokości przyklejonych (position: sticky) komórek pierwszego wiersza tabeli. */
const szerokoscPrzyklejonych = (otoczka) => {
    if (typeof window === "undefined" || typeof window.getComputedStyle !== "function") return 0;
    const tabela = otoczka.querySelector(":scope > table");
    if (!tabela) return 0;
    const pierwszyWiersz = tabela.querySelector("thead tr, tbody tr");
    if (!pierwszyWiersz) return 0;
    let suma = 0;
    for (const komorka of pierwszyWiersz.children) {
        const cs = window.getComputedStyle(komorka);
        if (cs.position === "sticky" && cs.left !== "auto") suma += komorka.offsetWidth || 0;
    }
    return suma;
};

/** Krok przewinięcia w px: ~80 % widocznej szerokości bez przyklejonych kolumn, nie mniej niż MIN_KROK_PX. */
export const krokPrzewiniecia = (clientWidth, przyklejone = 0) =>
    Math.max(MIN_KROK_PX, Math.round(Math.max(0, clientWidth - przyklejone) * 0.8));

export default function PrzewijanieTabeli({ className = "", etykieta = "Przewiń tabelę" }) {
    const refPaska = useRef(null);
    const [stan, setStan] = useState(STAN_POCZATKOWY);

    const otoczka = useCallback(() => refPaska.current?.parentElement || null, []);

    const zmierz = useCallback(() => {
        const el = otoczka();
        const nowy = zmierzPrzewijanie(el);
        // Tabela bez otoczki-przewijarki: włącz przewijanie dopiero, gdy jest co przewijać.
        if (el && nowy.mozna && !kontenerPrzewija(el)) el.style.overflowX = "auto";
        setStan((prev) => (prev.mozna === nowy.mozna && prev.naLewo === nowy.naLewo && prev.naPrawo === nowy.naPrawo
            ? prev
            : nowy));
    }, [otoczka]);

    const przewin = useCallback((kierunek) => {
        const el = otoczka();
        if (!el) return;
        const krok = kierunek * krokPrzewiniecia(el.clientWidth, szerokoscPrzyklejonych(el));
        if (typeof el.scrollBy === "function") {
            el.scrollBy({ left: krok, behavior: "smooth" });
        } else {
            el.scrollLeft += krok;
        }
        zmierz();
    }, [otoczka, zmierz]);

    useEffect(() => {
        const el = otoczka();
        if (!el) return undefined;
        zmierz();

        el.addEventListener("scroll", zmierz, { passive: true });
        window.addEventListener("resize", zmierz);

        let ro = null;
        let mo = null;
        if (typeof ResizeObserver === "function") {
            ro = new ResizeObserver(zmierz);
            ro.observe(el);
            const obserwujTabele = () => {
                el.querySelectorAll(":scope > table").forEach((t) => ro.observe(t));
            };
            obserwujTabele();
            if (typeof MutationObserver === "function") {
                // Tabela podmieniona (np. inny `key` po zmianie widoku) — obserwuj nową.
                mo = new MutationObserver(() => { obserwujTabele(); zmierz(); });
                mo.observe(el, { childList: true });
            }
        }
        return () => {
            el.removeEventListener("scroll", zmierz);
            window.removeEventListener("resize", zmierz);
            if (ro) ro.disconnect();
            if (mo) mo.disconnect();
        };
    }, [otoczka, zmierz]);

    return (
        <div
            ref={refPaska}
            className={`${p.pasek} ${className}`.trim()}
            hidden={!stan.mozna}
            data-przewijanie-tabeli={stan.mozna ? "aktywne" : "ukryte"}
        >
            {stan.mozna && (
                <>
                    <span className={p.podpowiedz}>{etykieta}</span>
                    <button
                        type="button"
                        className={p.przycisk}
                        onClick={() => przewin(-1)}
                        disabled={!stan.naLewo}
                        aria-disabled={!stan.naLewo}
                        title="Przewiń w lewo"
                        aria-label="Przewiń tabelę w lewo"
                    >
                        <FiChevronLeft size={16} />
                    </button>
                    <button
                        type="button"
                        className={p.przycisk}
                        onClick={() => przewin(1)}
                        disabled={!stan.naPrawo}
                        aria-disabled={!stan.naPrawo}
                        title="Przewiń w prawo"
                        aria-label="Przewiń tabelę w prawo"
                    >
                        <FiChevronRight size={16} />
                    </button>
                </>
            )}
        </div>
    );
}
